/**
 * Per-Drone Calendar Component - Phase X2
 * 
 * Custom calendar implementation using CSS Grid.
 * Shows month/week/day views with missions as events.
 * No external calendar library required (Tier C compatible).
 */

import { useState, useMemo, useCallback } from 'react';
import dayjs from 'dayjs';
import 'dayjs/locale/cs';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  ChevronLeft, ChevronRight, Calendar as CalendarIcon,
  Loader2, Circle, Wrench
} from 'lucide-react';
import { STATUS_COLORS, SAIL_COLORS, getSailBadgeLabel, getTelemetryIndicator } from '../../lib/calendar-utils';

// Maintenance color
const MAINTENANCE_COLOR = '#9ca3af';

/**
 * Event component for calendar cell (supports both mission and maintenance)
 */
const EventItem = ({ event, onClick, language }) => {
  const isMaintenance = event.event_type === 'maintenance';
  const status = event.status || 'draft';
  const backgroundColor = isMaintenance ? MAINTENANCE_COLOR : (STATUS_COLORS[status] || STATUS_COLORS.draft);
  
  // Maintenance has diagonal stripe pattern
  const stripeStyle = isMaintenance ? {
    backgroundImage: `repeating-linear-gradient(
      45deg,
      transparent,
      transparent 3px,
      rgba(255,255,255,0.15) 3px,
      rgba(255,255,255,0.15) 6px
    )`
  } : {};
  
  const title = isMaintenance 
    ? event.mission_name || event.reason 
    : `${event.mission_code} - ${event.mission_name}`;
  
  const displayText = isMaintenance 
    ? (language === 'cs' ? 'Údržba' : 'Maint.')
    : event.mission_code;
  
  return (
    <div
      className={`px-1 py-0.5 mb-0.5 rounded text-xs cursor-pointer hover:opacity-80 truncate ${isMaintenance ? 'opacity-90' : ''}`}
      style={{ backgroundColor, color: '#fff', ...stripeStyle }}
      onClick={(e) => { e.stopPropagation(); onClick?.(event); }}
      title={title}
      data-testid={isMaintenance ? 'maintenance-event' : 'mission-event'}
    >
      <span className="font-semibold">{displayText}</span>
      {!isMaintenance && event.pilot_initials && (
        <span className="ml-1 opacity-80">{event.pilot_initials}</span>
      )}
      {!isMaintenance && event.sail_class && (
        <Badge
          className="ml-1 text-[8px] px-0.5 py-0 h-3"
          style={{ backgroundColor: SAIL_COLORS[event.sail_class] || '#9ca3af' }}
          title={event.sail_provisional ? (language === 'cs' ? 'Předběžný odhad ze zjednodušeného výpočtu' : 'Preliminary estimate from a simplified calculation') : undefined}
        >
          {getSailBadgeLabel(event.sail_class, language, event.sail_provisional)}
        </Badge>
      )}
      {isMaintenance && (
        <Wrench className="inline-block ml-1 h-3 w-3" />
      )}
    </div>
  );
};

/**
 * Month view calendar grid
 */
const MonthView = ({ currentDate, events, onEventClick, onSlotClick, language, drone, translations }) => {
  dayjs.locale(language === 'cs' ? 'cs' : 'en');
  const more = translations?.more || (language === 'cs' ? 'dalších' : 'more');
  
  const { weeks, daysOfWeek } = useMemo(() => {
    const startOfMonth = currentDate.startOf('month');
    const endOfMonth = currentDate.endOf('month');
    const startOfCalendar = startOfMonth.startOf('week');
    const endOfCalendar = endOfMonth.endOf('week');
    
    // Day names
    const daysOfWeek = [];
    for (let i = 0; i < 7; i++) {
      daysOfWeek.push(startOfCalendar.add(i, 'day').format('ddd'));
    }
    
    // Generate weeks
    const weeks = [];
    let current = startOfCalendar;
    
    while (current.isBefore(endOfCalendar) || current.isSame(endOfCalendar)) {
      const week = [];
      for (let i = 0; i < 7; i++) {
        week.push(current);
        current = current.add(1, 'day');
      }
      weeks.push(week);
    }
    
    return { weeks, daysOfWeek };
  }, [currentDate, language]);
  
  // Group events by date
  const eventsByDate = useMemo(() => {
    const byDate = {};
    events.forEach(event => {
      const dateKey = dayjs(event.scheduled_start || event.mission_date).format('YYYY-MM-DD');
      if (!byDate[dateKey]) byDate[dateKey] = [];
      byDate[dateKey].push(event);
    });
    return byDate;
  }, [events]);
  
  const handleCellClick = (date) => {
    if (onSlotClick && drone) {
      onSlotClick({
        aircraft_id: drone.id,
        etot: date.hour(9).toISOString(),
        eldt: date.hour(11).toISOString()
      });
    }
  };
  
  return (
    <div className="border rounded-lg overflow-hidden">
      {/* Day headers */}
      <div className="grid grid-cols-7 bg-gray-100 dark:bg-gray-800 border-b">
        {daysOfWeek.map((day, i) => (
          <div key={i} className="p-2 text-center text-sm font-medium border-r last:border-r-0">
            {day}
          </div>
        ))}
      </div>
      
      {/* Weeks */}
      {weeks.map((week, wi) => (
        <div key={wi} className="grid grid-cols-7 border-b last:border-b-0">
          {week.map((day, di) => {
            const dateKey = day.format('YYYY-MM-DD');
            const dayEvents = eventsByDate[dateKey] || [];
            const isCurrentMonth = day.month() === currentDate.month();
            const isToday = day.isSame(dayjs(), 'day');
            const isWeekend = [0, 6].includes(day.day());
            
            return (
              <div
                key={di}
                className={`min-h-[80px] p-1 border-r last:border-r-0 cursor-pointer hover:bg-blue-50 dark:hover:bg-blue-900/20
                  ${!isCurrentMonth ? 'bg-gray-50 dark:bg-gray-800/50 text-gray-400' : ''}
                  ${isWeekend ? 'bg-gray-50 dark:bg-gray-800/30' : ''}
                  ${isToday ? 'bg-blue-50 dark:bg-blue-900/20' : ''}
                `}
                onClick={() => handleCellClick(day)}
              >
                <div className={`text-sm mb-1 ${isToday ? 'font-bold text-blue-600' : ''}`}>
                  {day.date()}
                </div>
                <div className="space-y-0.5 overflow-hidden max-h-[60px]">
                  {dayEvents.slice(0, 3).map((event, i) => (
                    <EventItem 
                      key={event.mission_id || i} 
                      event={event} 
                      onClick={onEventClick}
                      language={language}
                    />
                  ))}
                  {dayEvents.length > 3 && (
                    <div className="text-xs text-gray-500 pl-1">
                      +{dayEvents.length - 3} {more}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
};

/**
 * Week view calendar with 15-minute slot resolution
 */
const WeekView = ({ currentDate, events, onEventClick, onSlotClick, language, drone, translations, show24h = false }) => {
  dayjs.locale(language === 'cs' ? 'cs' : 'en');
  const timeLabel = translations?.time || (language === 'cs' ? 'Čas' : 'Time');
  const [hoveredSlot, setHoveredSlot] = useState(null);
  
  const { days, hours } = useMemo(() => {
    const startOfWeek = currentDate.startOf('week');
    const days = [];
    for (let i = 0; i < 7; i++) {
      days.push(startOfWeek.add(i, 'day'));
    }
    
    // Default 06:00-22:00, or full 24h
    const hours = [];
    const startHour = show24h ? 0 : 6;
    const endHour = show24h ? 23 : 21;
    for (let h = startHour; h <= endHour; h++) {
      hours.push(h);
    }
    
    return { days, hours };
  }, [currentDate, show24h]);
  
  // Group events by day and hour
  const eventsBySlot = useMemo(() => {
    const bySlot = {};
    events.forEach(event => {
      const start = dayjs(event.scheduled_start || event.mission_date + 'T09:00:00');
      const dateKey = start.format('YYYY-MM-DD');
      const hour = start.hour();
      const key = `${dateKey}-${hour}`;
      if (!bySlot[key]) bySlot[key] = [];
      bySlot[key].push(event);
    });
    return bySlot;
  }, [events]);
  
  // Snap to nearest 15-minute boundary (rounding down)
  const snapTo15Min = (minutes) => {
    return Math.floor(minutes / 15) * 15;
  };
  
  // Handle cell click with 15-min snap
  const handleSlotClick = (day, hour, e) => {
    if (onSlotClick && drone) {
      // Calculate minute from click position within cell
      const rect = e.currentTarget.getBoundingClientRect();
      const relativeX = e.clientX - rect.left;
      const cellWidth = rect.width;
      const minuteOffset = snapTo15Min(Math.floor((relativeX / cellWidth) * 60));
      
      // Force clean time boundary with second=0, ms=0
      const slotTime = day.hour(hour).minute(minuteOffset).second(0).millisecond(0);
      onSlotClick({
        aircraft_id: drone.id,
        etot: slotTime.toISOString(),
        eldt: slotTime.add(2, 'hour').toISOString()
      });
    }
  };
  
  // Handle mouse move for hover indicator
  const handleMouseMove = (e, day, hour) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const relativeX = e.clientX - rect.left;
    const cellWidth = rect.width;
    const minuteOffset = snapTo15Min(Math.floor((relativeX / cellWidth) * 60));
    const percentLeft = (minuteOffset / 60) * 100;
    
    setHoveredSlot({
      day: day.format('YYYY-MM-DD'),
      hour,
      minute: minuteOffset,
      percentLeft,
      timeStr: `${String(hour).padStart(2, '0')}:${String(minuteOffset).padStart(2, '0')}`
    });
  };
  
  return (
    <div className="border rounded-lg overflow-auto">
      <table className="w-full min-w-[800px]">
        <thead>
          <tr className="bg-gray-100 dark:bg-gray-800">
            <th className="w-16 p-2 border-r text-xs">
              {timeLabel}
            </th>
            {days.map((day, i) => {
              const isToday = day.isSame(dayjs(), 'day');
              return (
                <th 
                  key={i} 
                  className={`p-2 border-r last:border-r-0 text-center ${isToday ? 'bg-blue-100 dark:bg-blue-900/30' : ''}`}
                >
                  <div className="font-medium">{day.format('ddd')}</div>
                  <div className={`text-lg ${isToday ? 'text-blue-600 font-bold' : ''}`}>
                    {day.format('D')}
                  </div>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {hours.map(hour => (
            <tr key={hour} className="border-t">
              <td className="p-1 text-xs text-gray-500 border-r text-center align-top">
                {String(hour).padStart(2, '0')}:00
              </td>
              {days.map((day, di) => {
                const key = `${day.format('YYYY-MM-DD')}-${hour}`;
                const slotEvents = eventsBySlot[key] || [];
                const isHovered = hoveredSlot?.day === day.format('YYYY-MM-DD') && hoveredSlot?.hour === hour;
                
                return (
                  <td 
                    key={di}
                    className="relative p-0.5 border-r last:border-r-0 align-top min-h-[40px] cursor-pointer hover:bg-blue-50 dark:hover:bg-blue-900/10 group"
                    onClick={(e) => handleSlotClick(day, hour, e)}
                    onMouseMove={(e) => handleMouseMove(e, day, hour)}
                    onMouseLeave={() => setHoveredSlot(null)}
                  >
                    {/* 15-min grid lines (visible on hover) */}
                    <div className="absolute inset-0 flex opacity-0 group-hover:opacity-100 pointer-events-none">
                      <div className="flex-1 border-r border-gray-200 dark:border-gray-700" />
                      <div className="flex-1 border-r border-gray-200 dark:border-gray-700" />
                      <div className="flex-1 border-r border-gray-200 dark:border-gray-700" />
                      <div className="flex-1" />
                    </div>
                    
                    {/* Hover time indicator */}
                    {isHovered && (
                      <>
                        <div 
                          className="absolute top-0 bottom-0 w-0.5 bg-blue-500 pointer-events-none z-10"
                          style={{ left: `${hoveredSlot.percentLeft}%` }}
                        />
                        <div 
                          className="absolute -top-6 bg-gray-800 text-white text-xs px-1.5 py-0.5 rounded pointer-events-none z-20 whitespace-nowrap"
                          style={{ left: `${hoveredSlot.percentLeft}%`, transform: 'translateX(-50%)' }}
                        >
                          {hoveredSlot.timeStr}
                        </div>
                      </>
                    )}
                    
                    {/* Events */}
                    <div className="relative z-5">
                      {slotEvents.map((event, i) => (
                        <EventItem 
                          key={event.mission_id || i} 
                          event={event} 
                          onClick={onEventClick}
                          language={language}
                        />
                      ))}
                    </div>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export const PerDroneCalendar = ({
  drone,
  events = [],
  loading = false,
  language = 'en',
  translations = {},
  onEventClick,
  onSlotClick,
  onNavigate
}) => {
  const [view, setView] = useState('month');
  const [currentDate, setCurrentDate] = useState(dayjs());
  const [show24h, setShow24h] = useState(false);
  
  dayjs.locale(language === 'cs' ? 'cs' : 'en');
  
  // Use translations from props, fallback to inline only if missing
  const t = {
    today: translations.today || (language === 'cs' ? 'Dnes' : 'Today'),
    week: translations.week || (language === 'cs' ? 'Týden' : 'Week'),
    month: translations.month || (language === 'cs' ? 'Měsíc' : 'Month'),
    missions: translations.missions || (language === 'cs' ? 'misí' : 'missions'),
    status: translations.status || (language === 'cs' ? 'Stav' : 'Status'),
    draft: translations.draft || (language === 'cs' ? 'Koncept' : 'Draft'),
    ready: translations.ready || (language === 'cs' ? 'Připraveno' : 'Ready'),
    active: translations.active || (language === 'cs' ? 'Probíhá' : 'Active'),
    completed: translations.completed || (language === 'cs' ? 'Dokončeno' : 'Done'),
    more: language === 'cs' ? 'dalších' : 'more',
    show24h: translations.show24h || (language === 'cs' ? 'Zobrazit celých 24h' : 'Show full 24h'),
    show16h: translations.show16h || (language === 'cs' ? 'Zobrazit 06-22h' : 'Show 06-22h')
  };
  
  // Navigation
  const navigate = useCallback((direction) => {
    let newDate;
    const unit = view === 'week' ? 'week' : 'month';
    newDate = direction === 'prev' 
      ? currentDate.subtract(1, unit) 
      : currentDate.add(1, unit);
    setCurrentDate(newDate);
    onNavigate?.(newDate.toDate(), view);
  }, [view, currentDate, onNavigate]);
  
  const goToToday = useCallback(() => {
    setCurrentDate(dayjs());
    onNavigate?.(new Date(), view);
  }, [view, onNavigate]);
  
  const headerLabel = useMemo(() => {
    if (view === 'week') {
      const start = currentDate.startOf('week');
      const end = currentDate.endOf('week');
      return `${start.format('D. MMM')} - ${end.format('D. MMM YYYY')}`;
    }
    return currentDate.format('MMMM YYYY');
  }, [view, currentDate]);
  
  const telemetry = getTelemetryIndicator(drone?.telemetry_status);
  
  return (
    <Card className="border-[var(--border-default)]" data-testid="per-drone-calendar">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <Circle 
              className={`h-3 w-3 ${telemetry.pulse ? 'animate-pulse' : ''}`}
              style={{ fill: telemetry.color, color: telemetry.color }}
            />
            <CalendarIcon className="h-5 w-5 text-blue-600" />
            {drone?.model || 'Drone'} - {drone?.registration_number || drone?.serial_number || ''}
          </CardTitle>
          <Badge variant="outline">{events.length} {t.missions}</Badge>
        </div>
        
        {/* Toolbar */}
        <div className="flex items-center justify-between mt-3">
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={goToToday}>
              {t.today}
            </Button>
            <Button variant="ghost" size="icon" onClick={() => navigate('prev')}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" onClick={() => navigate('next')}>
              <ChevronRight className="h-4 w-4" />
            </Button>
            {view === 'week' && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setShow24h(!show24h)}
                className="ml-2 text-xs"
                data-testid="toggle-24h"
              >
                {show24h ? t.show16h : t.show24h}
              </Button>
            )}
          </div>
          
          <span className="font-semibold">{headerLabel}</span>
          
          <div className="flex items-center gap-1">
            {['week', 'month'].map(v => (
              <Button
                key={v}
                variant={view === v ? 'default' : 'outline'}
                size="sm"
                onClick={() => {
                  setView(v);
                  onNavigate?.(currentDate.toDate(), v);
                }}
              >
                {t[v]}
              </Button>
            ))}
          </div>
        </div>
      </CardHeader>
      
      <CardContent>
        {loading ? (
          <div className="h-[400px] flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
          </div>
        ) : (
          <>
            {view === 'month' && (
              <MonthView 
                currentDate={currentDate}
                events={events}
                onEventClick={onEventClick}
                onSlotClick={onSlotClick}
                language={language}
                drone={drone}
                translations={t}
              />
            )}
            {view === 'week' && (
              <WeekView 
                currentDate={currentDate}
                events={events}
                onEventClick={onEventClick}
                onSlotClick={onSlotClick}
                language={language}
                drone={drone}
                translations={t}
                show24h={show24h}
              />
            )}
          </>
        )}
        
        {/* Legend */}
        <div className="mt-3 pt-3 border-t flex flex-wrap gap-4 text-xs" data-testid="calendar-legend">
          <span className="font-medium">{t.status}:</span>
          {Object.entries(STATUS_COLORS).map(([status, color]) => (
            <span key={status} className="flex items-center gap-1">
              <span className="w-3 h-3 rounded" style={{ backgroundColor: color }} />
              {t[status] || status}
            </span>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default PerDroneCalendar;
